"use client";
import { ICONS } from "@/constants/constants";
import Image from "next/image";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setUserInfo, logout } from "@/lib/slices/authSlice";
import { viewUserProfile } from "@/lib/api/user";
import { viewAdminProfile } from "@/lib/api/admin";
import Link from "next/link";

import { useRouter, usePathname } from "next/navigation";
import { X } from "lucide-react";
import { useToastContext } from "@/components/ui/ToastProvider";
import { isSessionValid, clearAuthCookies, decodeJWT } from "@/lib/utils/jwt";
import { UserRole } from "@/types/roles";
import Cookies from "js-cookie";
import TruncatedText from "./atomic/TruncatedText";
import useTruncate from "@/hooks/useTruncate";

const Navbar = () => {
  const dispatch = useDispatch();
  const router = useRouter();
  const pathname = usePathname();
  const { toast } = useToastContext();
  const user = useSelector((state: any) => state.auth.user);

  // Get environment indicator based on current domain
  const getEnvironmentInfo = () => {
    if (typeof window === "undefined") return null;
    
    const hostname = window.location.hostname;
    
    if (hostname === "localhost" || hostname === "127.0.0.1") {
      return { label: "localhost", color: "bg-purple-200 text-purple-800" };
    }
    if (hostname.includes("dev.")) {
      return { label: "DEV", color: "bg-orange-200 text-orange-800" };
    }
    if (hostname.includes("qa.")) {
      return { label: "QA", color: "bg-blue-200 text-blue-800" };
    }
    if (hostname.includes("stag") || hostname.includes("staging")) {
      return { label: "STAGING", color: "bg-yellow-200 text-yellow-800" };
    }
    // Production - don't show indicator
    return null;
  };

  const [envInfo, setEnvInfo] = useState<{ label: string; color: string } | null>(null);

  useEffect(() => {
    setEnvInfo(getEnvironmentInfo());
  }, []);
  const isAdmin =
    user && user.role && user.role.toUpperCase().includes(UserRole.ADMIN);
  const isCashier =
    user && user.role && user.role.toUpperCase().includes(UserRole.CASHIER);
  const isFinance =
    user && user.role && user.role.toUpperCase().includes(UserRole.FINANCE);
  const isStandard =
    user && user.role && user.role.toUpperCase().includes(UserRole.STANDARD);
  const hasAdminPrivileges = isAdmin || isCashier || isFinance || isStandard;
  const [isLoading, setIsLoading] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [profileFetched, setProfileFetched] = useState(false);
  const isMountedRef = useRef(true);
  // Prevent repeated profile fetch attempts (e.g. when API errors occur)
  const fetchAttemptedRef = useRef(false);

  // Truncate user name to 25 characters
  const fullName = user ? `${user.firstName} ${user.lastName}` : "";
  const { displayText: truncatedName } = useTruncate(fullName, 25);

  // Cleanup function to prevent memory leaks
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Memoized profile fetch function
  const fetchProfile = useCallback(async () => {
    if (!isMountedRef.current) return;

    try {
      // Mark that we've attempted a fetch so we don't retry continuously on error
      fetchAttemptedRef.current = true;
      // Get token and decode to check role
      const accessToken = Cookies.get("accessToken");
      if (!accessToken || !isMountedRef.current) {
        return;
      }

      const decodedToken = decodeJWT(accessToken);
      if (!decodedToken || !decodedToken.role || !isMountedRef.current) {
        return;
      }

      const userRole = decodedToken.role.toUpperCase();
      const adminRoles = [
        UserRole.SUPER_ADMIN,
        UserRole.ADMIN,
        UserRole.CASHIER,
        UserRole.FINANCE,
        UserRole.STANDARD,
      ];

      let response;
      if (adminRoles.includes(userRole as UserRole)) {
        // User has admin privileges, call admin profile API
        response = await viewAdminProfile();
      } else if (
        userRole === UserRole.USER ||
        userRole === UserRole.PASSENGER
      ) {
        // Regular user or passenger, call user profile API
        response = await viewUserProfile();
      }

      if (
        response &&
        response.success &&
        response.data &&
        response.data.user &&
        isMountedRef.current
      ) {
        dispatch(setUserInfo(response.data.user));
        setProfileFetched(true);
      }
    } catch (err: any) {
      if (!isMountedRef.current) return;

      // Check if error is due to session expiration
      if (err?.response?.status === 401) {
        clearAuthCookies();
        toast.error(err.response.data.message);
        setTimeout(() => {
          dispatch(logout());
          setProfileFetched(false);
        }, 500);

        // Get current user role for redirect
        const accessToken = Cookies.get("accessToken");
        if (accessToken) {
          const decodedToken = decodeJWT(accessToken);
          const userRole = decodedToken?.role?.toUpperCase();
          const adminRoles = [
            UserRole.ADMIN,
            UserRole.CASHIER,
            UserRole.FINANCE,
            UserRole.STANDARD,
          ];

          if (adminRoles.includes(userRole as UserRole)) {
            router.push("/admin/sign-in");
          } else {
            router.push("/sign-in");
          }
        } else {
          router.push("/sign-in");
        }
        return;
      }

      if (err?.response?.status === 403) {
        clearAuthCookies();
        toast.error(err.response.data.message);
        setTimeout(() => {
          dispatch(logout());
          setProfileFetched(false);
        }, 500);

        // Get current user role for redirect
        const accessToken = Cookies.get("accessToken");
        if (accessToken) {
          const decodedToken = decodeJWT(accessToken);
          const userRole = decodedToken?.role?.toUpperCase();
          const adminRoles = [
            UserRole.ADMIN,
            UserRole.CASHIER,
            UserRole.FINANCE,
            UserRole.STANDARD,
          ];

          if (adminRoles.includes(userRole as UserRole)) {
            router.push("/admin/sign-in");
          } else {
            router.push("/sign-in");
          }
        } else {
          router.push("/sign-in");
        }
        return;
      }

      toast.error(
        "",
        err?.response?.data?.message ||
          "Something went wrong. Please try again."
      );
    } finally {
      if (isMountedRef.current) {
        // Always set loading to false, regardless of success or failure
        setIsLoading(false);
      }
    }
  }, [dispatch, toast, router]);

  useEffect(() => {
    // Check session validity first
    if (!isSessionValid() && user) {
      // Session expired, clear everything and show toast
      clearAuthCookies();
      dispatch(logout());
      toast.error("", "Your session has expired. Please sign in again.");
      setProfileFetched(false); // Reset profile fetched state

      // Redirect to appropriate login page
      if (hasAdminPrivileges) {
        router.push("/admin/sign-in");
      } else {
        router.push("/sign-in");
      }
      return;
    }

    // Only fetch profile if user is not already in Redux AND not currently loading AND session is valid
    // Also ensure we haven't already attempted a fetch to avoid infinite retry loops on error
    if (!user && !isLoading && isSessionValid() && !fetchAttemptedRef.current) {
      setIsLoading(true);
      setProfileFetched(false); // Reset before fetching
      // mark attempted before calling to avoid race conditions
      fetchAttemptedRef.current = true;
      fetchProfile();
    }

    // Reset profileFetched when user logs out
    if (!user && profileFetched) {
      setProfileFetched(false);
    }
  }, [
    dispatch,
    user,
    toast,
    router,
    hasAdminPrivileges,
    fetchProfile,
    isLoading,
    profileFetched,
  ]);

  // Reset fetchAttemptedRef when user logs out or changes so we can attempt fetching again later
  useEffect(() => {
    if (!user) {
      fetchAttemptedRef.current = false;
    }
  }, [user]);

  const adminLinks = [
    { name: "Dashboard", href: "/admin/dashboard" },
    { name: "Refund", href: "/admin/refund" },
    { name: "Content", href: "/admin/content" },
    { name: "Schedule", href: "/admin/schedule" },
    { name: "Fares", href: "/admin/fares" },
    { name: "Users", href: "/admin/users" },
    { name: "Reports", href: "/admin/reports" },
    { name: "Logout", href: null }, // href is null for logout
  ];

  const cashierLinks = [
    { name: "Dashboard", href: "/admin/dashboard" },
    { name: "Refund", href: "/admin/refund" },
    { name: "Logout", href: null }, // href is null for logout
  ];

  const financeLinks = [
    { name: "Dashboard", href: "/admin/dashboard" },
    { name: "Fares", href: "/admin/fares" },
    { name: "Reports", href: "/admin/reports" },
    { name: "Logout", href: null }, // href is null for logout
  ];

  const standardLinks = [
    { name: "Dashboard", href: "/admin/dashboard" },
    { name: "Logout", href: null }, // href is null for logout
  ];

  const userLinks = [
    { name: "Schedule", href: "/schedule" },
    { name: "Fares", href: "/fares" },
    { name: "Our history", href: "/our-history" },
    { name: "Contact", href: "/contact-us" },
    { name: "FAQ", href: "/faq" },
    user
      ? { name: truncatedName, href: "/dashboard" }
      : { name: "Login", href: "/sign-in" },
  ];

  const guestLinks = [
    { name: "Schedule", href: "/schedule" },
    { name: "Fares", href: "/fares" },
    { name: "Our history", href: "/our-history" },
    { name: "Contact", href: "/contact-us" },
    { name: "FAQ", href: "/faq" },
    { name: "Login", href: "/sign-in" },
  ];

  const getLinks = () => {
    // If loading, return empty array to show skeleton
    if (isLoading) return [];

    // If user exists, return role-based links
    if (user) {
      if (isAdmin) return adminLinks;
      if (isCashier) return cashierLinks;
      if (isFinance) return financeLinks;
      if (isStandard) return standardLinks;
      return userLinks;
    }

    // Fallback to guest links if no user or error
    return guestLinks;
  };

  const links = getLinks();

  // Skeleton loader component
  const NavSkeleton = () => (
    <div className="hidden xl:flex items-center gap-8">
      {[...Array(5)].map((_, index) => (
        <div
          key={index}
          className="h-4 bg-gray-200 rounded animate-pulse"
          style={{ width: `${60 + Math.random() * 40}px` }}
        />
      ))}
    </div>
  );

  // Mobile skeleton loader component
  const MobileSkeleton = () => (
    <div className="flex flex-col p-6 space-y-6 bg-skin">
      {[...Array(6)].map((_, index) => (
        <div
          key={index}
          className="h-6 bg-gray-200 rounded animate-pulse"
          style={{ width: `${80 + Math.random() * 60}px` }}
        />
      ))}
    </div>
  );

  const handleAuthorize = () => {
    // Clear cookies from all possible paths immediately
    Cookies.remove("accessToken");
    Cookies.remove("refreshToken");
    Cookies.remove("accessToken", { path: "/" });
    Cookies.remove("refreshToken", { path: "/" });

    // Reset fetch guard when explicitly logging out
    fetchAttemptedRef.current = false;

    // Clear Redux state
    dispatch(logout());

    // Force immediate navigation based on user role
    if (hasAdminPrivileges) {
      router.push("/admin/sign-in");
    } else {
      router.push("/sign-in");
    }
  };
  // Logout handler
  const handleLogout = () => {
    // Clear cookies from all possible paths immediately
    Cookies.remove("accessToken");
    Cookies.remove("refreshToken");
    Cookies.remove("accessToken", { path: "/" });
    Cookies.remove("refreshToken", { path: "/" });

    // Reset fetch guard when explicitly logging out
    fetchAttemptedRef.current = false;

    // Clear Redux state
    dispatch(logout());

    // Show success message
    toast.success("You have been logged out successfully.");

    // Force immediate navigation based on user role
    if (hasAdminPrivileges) {
      router.push("/admin/sign-in");
    } else {
      router.push("/sign-in");
    }
  };

  // Helper to check if a link is active
  const isActive = (href: string | null) => {
    if (!href) return false;
    // Exact match or startsWith for admin subroutes
    return pathname === href || (href !== "/" && pathname.startsWith(href));
  };

  return (
    <>
      <nav className="w-full flex items-center justify-between px-4 md:px-8 py-2 bg-white font-work-sans shadow-xs">
        {/* Logo (with text) on the left */}
        <div className="flex items-center max-sm:flex-col">
          <Image
            onClick={() => router.push("/")}
            className="cursor-pointer"
            src={ICONS.mainlogo}
            alt="mainlogo"
            width={180}
            height={40}
            priority
          />
          {/* Environment indicator */}
          {envInfo && (
            <span className={`ml-2 text-xs px-2 py-0.5 rounded font-medium ${envInfo.color}`}>
              {envInfo.label}
            </span>
          )}
          {isAdmin && (
            <Link href={"/admin/dashboard"}>
              <span className="ml-4 text-lg bg-gray-200 pr-3 pl-3 pt-1 pb-1 rounded text-[#151C28] tracking-wide">
                admin
              </span>
            </Link>
          )}
          {isCashier && (
            <Link href={"/admin/dashboard"}>
              <span className="ml-4 text-lg bg-blue-200 pr-3 pl-3 pt-1 pb-1 rounded text-[#151C28] tracking-wide">
                cashier
              </span>
            </Link>
          )}
          {isFinance && (
            <Link href={"/admin/dashboard"}>
              <span className="ml-4 text-lg bg-green-200 pr-3 pl-3 pt-1 pb-1 rounded text-[#151C28] tracking-wide">
                finance
              </span>
            </Link>
          )}
          {isStandard && (
            <Link href={"/admin/dashboard"}>
              <span className="ml-4 text-lg bg-yellow-200 pr-3 pl-3 pt-1 pb-1 rounded text-[#151C28] tracking-wide">
                standard
              </span>
            </Link>
          )}
        </div>

        {/* Desktop Navigation links */}
        {isLoading ? (
          <NavSkeleton />
        ) : (
          <div className="hidden xl:flex items-center gap-8 text-custom-gray-light">
            {links.map((link) =>
              link.name === "Logout" ? (
                <button
                  key={link.name}
                  onClick={handleLogout}
                  className="text-gray-500 text-base font-normal hover:text-gray-800 transition-colors bg-transparent border-none cursor-pointer"
                >
                  {link.name}
                </button>
              ) : (
                typeof link.href === "string" && (
                  <Link
                    key={link.name}
                    href={link.href}
                    className={
                      isActive(link.href)
                        ? "text-[#4B5563] font-bold underline underline-offset-4 decoration-2 decoration-[#4B5563] "
                        : "text-gray-500 text-base font-normal hover:text-gray-800 transition-colors"
                    }
                    style={isActive(link.href) ? { paddingBottom: 2 } : {}}
                  >
                    {link.name}
                  </Link>
                )
              )
            )}
          </div>
        )}

        {/* Mobile hamburger menu button */}
        <button
          className="block xl:hidden p-2"
          onClick={() => setIsMobileMenuOpen(true)}
          aria-label="Open menu"
        >
          <Image src={ICONS.hamburger} alt="Menu" width={20} height={20} />
        </button>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-50 xl:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setIsMobileMenuOpen(false)}
          />

          {/* Menu Panel */}
          <div className="absolute right-0 top-0 h-full w-full shadow-lg">
            {/* Menu Header */}
            <div className="flex items-center justify-between p-6  bg-white">
              <h2 className="text-2xl font-bold text-[#151C28] font-rakesly">
                Menu
              </h2>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2"
                aria-label="Close menu"
              >
                <X />
              </button>
            </div>

            {/* Menu Items */}
            {isLoading ? (
              <MobileSkeleton />
            ) : (
              <div className="flex flex-col p-6 space-y-6 bg-skin">
                {links.map((link) =>
                  link.name === "Logout" ? (
                    <button
                      key={link.name}
                      onClick={() => {
                        handleLogout();
                        setIsMobileMenuOpen(false);
                      }}
                      className="w-full bg-[#151C28] text-white py-3 px-6 rounded-lg font-medium text-center flex items-center justify-center gap-2"
                    >
                      <Image
                        src={ICONS.person}
                        alt="Login"
                        width={20}
                        height={20}
                      />
                      {link.name}
                    </button>
                  ) : (
                    typeof link.href === "string" && (
                      <Link
                        key={link.name}
                        href={link.href}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="text-[#6B7280] text-lg font-medium py-2 hover:text-[#151C28] transition-colors"
                      >
                        {link.name}
                      </Link>
                    )
                  )
                )}

                {/* Login button for non-authenticated users */}
                {!user && !isLoading && (
                  <Link
                    href="/sign-in"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="w-full bg-[#151C28] text-white py-3 px-6 rounded-lg font-medium text-center flex items-center justify-center gap-2 mt-8"
                  >
                    <Image
                      src={ICONS.person}
                      alt="Login"
                      width={20}
                      height={20}
                    />
                    Login
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;